import type { StoryFormatId } from './storyFormats';

/**
 * The hero layouts of a Story (design `asset-redesign/b Story - new`): the release date and the
 * cover (pre-save), the cover and "Out now" (out now), the lyric as big type (lyric line), and a
 * huge day number (the countdown stills d7, d3, d1). A feed post (design `e Feed post - new`) has
 * the first three and `announce`: "New single", the release date in a ring, the cover.
 */
export const KIT_STORY_MOMENTS = ['preSave', 'outNow', 'lyric', 'countdown', 'announce'] as const;
export type KitStoryMoment = (typeof KIT_STORY_MOMENTS)[number];

/**
 * How a Story draws the cover (design `studio-details/a Image details`, frame a1): `card` the cover
 * in the frame of the theme (the Cover card style), `ground` the blurred cover behind a scrim and
 * no cover card (Cover ground), `type` no cover at all (Type only). The type grows when the cover
 * card goes.
 */
export const STORY_STYLES = ['card', 'ground', 'type'] as const;
export type StoryStyle = (typeof STORY_STYLES)[number];

/**
 * The texts of one Story. Code writes them (music24-api `buildKitStory`), from the song, the release
 * date, the moment and the SmartLink of the song. The layout (`storyLayout`) places them; the theme
 * frame, the order, the sizes and the colours come from code, never from a plan.
 */
export interface KitStory {
  /**
   * The frame of the stack: a Story (9:16), a 4:5 feed post or a square feed post (the 4:5 stack at
   * 0.8). Absent: a Story.
   */
  format?: StoryFormatId;
  moment: KitStoryMoment;
  title: string;
  artist: string;
  /** The label above the cover of a pre-save Story ("New single"). Null: no kicker. */
  kicker: string | null;
  /**
   * The hero text: the date label of a pre-save Story ("16 Oct"), the hero word of an out-now Story
   * ("Out now"), the lyric (one line, or two lines with a line break), or the day number of a
   * countdown ("7"). Null: a pre-save Story of a song with no release date, which shows no date.
   */
  hero: string | null;
  /** The word of the lyric that lights in the accent (the "now word" of the theme). Null: none. */
  nowWord: string | null;
  /** The line under the day number of a countdown ("days to go", "day to go"). */
  dayLabel: string | null;
  /**
   * The date ring of a countdown ("Out 16 Oct", "Tomorrow · 16 Oct") and of an announce feed post
   * ("Out Fri 16 Oct"). Null: the date is not known.
   */
  when: string | null;
  /** The text of the CTA pill ("Pre-save now", "Listen now", "Coming soon"). */
  cta: string;
  /**
   * The words before the address under the pill of a feed post ("Link in bio"; owner answer
   * 2026-10-08: a link in a feed image cannot be tapped, so the post shows both). Absent or null:
   * the address alone.
   */
  linkNote?: string | null;
  /**
   * The published address of the SmartLink of the song, with no scheme
   * (`laziibee.music24.link/swamps`). Null: the song has no published SmartLink, so the Story shows
   * no address (the pill still shows).
   */
  link: string | null;
  /** True: the kit has a logo; it goes first in the safe column, at most 96 px high. */
  withLogo: boolean;
}

/** The Story style of an asset style id: Cover ground and Type only have their own layout; every other style is the card. */
export function storyStyleOf(assetStyle: string | null | undefined): StoryStyle {
  if (assetStyle === 'coverGround') {
    return 'ground';
  }
  return assetStyle === 'typeOnly' ? 'type' : 'card';
}
